import { useEffect, useState } from "react";
import { adminGetSettings, adminUpdateSettings, adminStripeStatus, adminSyncMissingGCal, adminSyncHolidaysGCal, adminResetAndResyncGCal, adminGCalJobStatus, adminSendTestEmail, adminTestCheckout } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import { Plus, Trash2, Info, Image as ImageIcon, X } from "lucide-react";
import MediaPicker from "../../components/MediaPicker";
import { extractErrorMessage } from "../../lib/errors";

export default function AdminSettings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [logoPickerOpen, setLogoPickerOpen] = useState(false);
  const [stripeStatus, setStripeStatus] = useState({ live_configured: false, live_key_last4: "" });
  const [gcalSyncing, setGcalSyncing] = useState(false);
  const [gcalResult, setGcalResult] = useState(null);
  const [holidaySyncing, setHolidaySyncing] = useState(false);
  const [holidayResult, setHolidayResult] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [resetResult, setResetResult] = useState(null);
  const [testEmailTo, setTestEmailTo] = useState("");
  const [testEmailSending, setTestEmailSending] = useState(false);
  const [testCheckout, setTestCheckout] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    date: "",
    time: "13:30",
    pickup_postcode: "",
  });
  const [testCheckoutLoading, setTestCheckoutLoading] = useState(false);
  const [testCheckoutResult, setTestCheckoutResult] = useState(null);

  useEffect(() => {
    adminGetSettings()
      .then(data => {
        if (!data.discount_tiers || data.discount_tiers.length === 0) {
          data.discount_tiers = [
            { min_lessons: 5, discount_per_hour: 1.0, label: "5+ lesson package" },
            { min_lessons: 10, discount_per_hour: 1.5, label: "10+ lesson package" },
          ];
        }
        setS(data);
      })
      .catch(() => toast.error("Could not load settings"));
    adminStripeStatus()
      .then(setStripeStatus)
      .catch(() => {});
  }, []);

  const onSave = async () => {
    try {
      setSaving(true);
      const tiers = (s.discount_tiers || [])
        .map(t => ({
          min_lessons: parseInt(t.min_lessons, 10) || 0,
          discount_per_hour: parseFloat(t.discount_per_hour) || 0,
          label: t.label || "",
        }))
        .filter(t => t.min_lessons > 0)
        .sort((a, b) => a.min_lessons - b.min_lessons);
      const payload = {
        manual_hourly: parseFloat(s.manual_hourly),
        auto_hourly: parseFloat(s.auto_hourly),
        discount_tiers: tiers,
        // Keep legacy fields aligned with tiers so old clients still see something sane
        discount_10h: tiers.find(t => t.min_lessons === 5)?.discount_per_hour ?? 0,
        discount_20h: tiers.find(t => t.min_lessons === 10)?.discount_per_hour ?? 0,
        instructor_phone: s.instructor_phone,
        instructor_email: s.instructor_email,
        instructor_name: s.instructor_name,
        header_logo_url: s.header_logo_url || "",
        stripe_mode: s.stripe_mode || "test",
      };
      await adminUpdateSettings(payload);
      toast.success("Settings saved — prices update on the site instantly.");
    } catch (err) {
      toast.error("Could not save settings");
    } finally { setSaving(false); }
  };

  const addTier = () => {
    setS(prev => ({ ...prev, discount_tiers: [...(prev.discount_tiers || []), { min_lessons: 0, discount_per_hour: 0, label: "" }] }));
  };
  const updateTier = (i, key, val) => {
    setS(prev => {
      const tiers = [...(prev.discount_tiers || [])];
      tiers[i] = { ...tiers[i], [key]: val };
      return { ...prev, discount_tiers: tiers };
    });
  };
  const removeTier = (i) => {
    setS(prev => ({ ...prev, discount_tiers: (prev.discount_tiers || []).filter((_, j) => j !== i) }));
  };

  const syncMissingGCal = async (onlyImports = true) => {
    const label = onlyImports
      ? "Push imported historical bookings to Google Calendar now? (Safe to re-run — duplicates are prevented.)"
      : "Push EVERY booking without a calendar event to Google Calendar now?";
    if (!window.confirm(label)) return;
    try {
      setGcalSyncing(true);
      setGcalResult(null);
      const res = await adminSyncMissingGCal(onlyImports);
      setGcalResult(res);
      toast.success(`Synced ${res.synced} booking${res.synced === 1 ? "" : "s"} to Google Calendar.`);
    } catch (err) {
      const msg = extractErrorMessage(err, "Sync failed");
      toast.error(msg);
    } finally {
      setGcalSyncing(false);
    }
  };

  const syncHolidaysGCal = async () => {
    if (!window.confirm("Push every blocked date / holiday to Google Calendar now? Safe to re-run — existing events are detected and updated, not duplicated.")) return;
    try {
      setHolidaySyncing(true);
      setHolidayResult(null);
      const res = await adminSyncHolidaysGCal();
      setHolidayResult(res);
      const total = (res.created || 0) + (res.updated || 0);
      toast.success(`Reconciled ${res.examined_dates} date${res.examined_dates === 1 ? "" : "s"} (${res.created} created, ${res.updated} updated, ${res.deleted} cleaned up).`);
    } catch (err) {
      toast.error(extractErrorMessage(err, "Holiday sync failed"));
    } finally {
      setHolidaySyncing(false);
    }
  };

  const resetAndResync = async () => {
    const ok = window.confirm(
      "DELETE every Google Calendar event the app has ever created, then rebuild from this site's database?\n\n" +
      "• Events you added manually in Google Calendar will NOT be touched.\n" +
      "• Use this to clean up duplicates from running syncs across multiple environments.\n\n" +
      "Continue?"
    );
    if (!ok) return;
    try {
      setResetting(true);
      setResetResult(null);
      await adminResetAndResyncGCal();
      toast.message("Reset started. This usually takes 1–3 minutes — watch the progress here, don't close the tab.");

      // Poll job status every 2s until done.
      const pollOnce = async () => {
        try {
          const status = await adminGCalJobStatus();
          setResetResult(status);
          if (status?.status === "completed") {
            toast.success(`Done · purged ${(status.purged?.booking_events_deleted || 0) + (status.purged?.holiday_events_deleted || 0)} old events, re-synced ${status.bookings_resynced} bookings + ${status.holiday_dates_resynced} blocked dates.`);
            return "stop";
          }
          if (status?.status === "failed") {
            toast.error(`Reset failed: ${status.error || "unknown error"}`);
            return "stop";
          }
          return "continue";
        } catch (err) {
          // Transient poll failure — keep going.
          return "continue";
        }
      };
      const interval = setInterval(async () => {
        const r = await pollOnce();
        if (r === "stop") {
          clearInterval(interval);
          setResetting(false);
        }
      }, 2000);
      // Also do an immediate first poll.
      const first = await pollOnce();
      if (first === "stop") {
        clearInterval(interval);
        setResetting(false);
      }
    } catch (err) {
      toast.error(extractErrorMessage(err, "Reset failed"));
      setResetting(false);
    }
  };

  const sendTestEmail = async () => {
    const to = testEmailTo.trim();
    if (!to || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(to)) {
      toast.error("Enter a valid recipient email first.");
      return;
    }
    try {
      setTestEmailSending(true);
      const res = await adminSendTestEmail(to);
      toast.success(`Test email queued to ${to} (id ${res.email_id?.slice(0, 8)}…).`);
    } catch (err) {
      const msg = extractErrorMessage(err, "Send failed");
      toast.error(msg);
    } finally {
      setTestEmailSending(false);
    }
  };

  const createTestCheckout = async () => {
    const { customer_name, customer_email, customer_phone, date, time } = testCheckout;
    if (!customer_name || !customer_email || !customer_phone || !date || !time) {
      toast.error("Fill name, email, phone, date and time first.");
      return;
    }
    const mode = s.stripe_mode === "live" ? "LIVE" : "Sandbox";
    const ok = window.confirm(
      `Create a £1 ${mode} Stripe checkout for ${date} ${time}?\n\n` +
      (mode === "LIVE"
        ? "You will be charged £1 from a real card. Refund it afterwards from the order in /admin/calendar."
        : "No real money — test card 4242 4242 4242 4242 works.")
    );
    if (!ok) return;
    try {
      setTestCheckoutLoading(true);
      setTestCheckoutResult(null);
      const res = await adminTestCheckout(testCheckout);
      setTestCheckoutResult(res);
      toast.success(`£1 checkout ready (mode: ${res.stripe_mode}). Open the link.`);
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not create test checkout"));
    } finally {
      setTestCheckoutLoading(false);
    }
  };

  if (!s) return <div className="text-[#9CA3AF]">Loading…</div>;

  // Preview the cost of every product so admin sees the effect of changes
  const slotsForProduct = { standard: 1, package10: 5, package20: 10 };
  const previewPrice = (slots, transmission) => {
    const base = transmission === "manual" ? parseFloat(s.manual_hourly) || 0 : parseFloat(s.auto_hourly) || 0;
    const tiers = (s.discount_tiers || []).map(t => ({
      min_lessons: parseInt(t.min_lessons, 10) || 0,
      discount_per_hour: parseFloat(t.discount_per_hour) || 0,
    }));
    let best = 0;
    tiers.forEach(t => { if (slots >= t.min_lessons && t.discount_per_hour > best) best = t.discount_per_hour; });
    const rate = Math.max(0, base - best);
    return { rate, total: rate * slots * 2, discount: best };
  };

  return (
    <div data-testid="admin-settings-page">
      <h1 className="text-2xl tracking-tight font-medium" style={{fontFamily:"Outfit, sans-serif"}}>Pricing &amp; settings</h1>
      <p className="text-sm text-[#4B5563]">Customers see these prices instantly at checkout.</p>

      <div className="mt-6 grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-7 space-y-4 bg-white border border-black/5 rounded-2xl p-6">
          <h2 className="text-base font-medium" style={{fontFamily:"Outfit, sans-serif"}}>Hourly rates</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs">Manual £/hr</Label>
              <Input type="number" step="0.5" value={s.manual_hourly} onChange={e => setS({...s, manual_hourly: e.target.value})} data-testid="settings-manual" />
            </div>
            <div>
              <Label className="text-xs">Automatic £/hr</Label>
              <Input type="number" step="0.5" value={s.auto_hourly} onChange={e => setS({...s, auto_hourly: e.target.value})} data-testid="settings-auto" />
            </div>
          </div>

          <div className="border-t border-black/5 pt-4">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-medium" style={{fontFamily:"Outfit, sans-serif"}}>Bulk discount tiers</h2>
              <Button type="button" size="sm" variant="outline" className="border-black/15" onClick={addTier} data-testid="add-tier-btn">
                <Plus className="h-4 w-4 mr-1" /> Add tier
              </Button>
            </div>
            <div className="text-xs text-[#4B5563] mt-2 flex items-start gap-2">
              <Info className="h-3.5 w-3.5 mt-0.5 flex-none text-[#37B160]" />
              <span>When a customer books N or more 2-hour lessons, they get this £-per-hour reduction off the base rate. The highest matching tier applies. <strong>1 lesson = 1 × 2-hour slot.</strong></span>
            </div>

            <div className="mt-3 space-y-2">
              {(s.discount_tiers || []).map((t, i) => (
                <div key={i} className="grid grid-cols-[1fr_1fr_2fr_auto] gap-2 items-end bg-[#F3F4F6] rounded-xl p-3" data-testid={`tier-row-${i}`}>
                  <div>
                    <Label className="text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF]">When ≥</Label>
                    <Input type="number" min="1" value={t.min_lessons} onChange={e => updateTier(i, "min_lessons", e.target.value)} className="h-9" data-testid={`tier-min-${i}`} />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF]">£ off / hour</Label>
                    <Input type="number" min="0" step="0.5" value={t.discount_per_hour} onChange={e => updateTier(i, "discount_per_hour", e.target.value)} className="h-9" data-testid={`tier-discount-${i}`} />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF]">Label (optional)</Label>
                    <Input value={t.label} onChange={e => updateTier(i, "label", e.target.value)} className="h-9" placeholder="e.g. 5+ lesson package" />
                  </div>
                  <Button type="button" size="sm" variant="ghost" onClick={() => removeTier(i)} className="text-red-500 hover:text-red-600" data-testid={`tier-remove-${i}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              {(s.discount_tiers || []).length === 0 && (
                <div className="text-sm text-[#9CA3AF] py-3">No discount tiers — customers always pay the full hourly rate.</div>
              )}
            </div>
          </div>

          <div className="border-t border-black/5 pt-4 space-y-3">
            <h2 className="text-base font-medium" style={{fontFamily:"Outfit, sans-serif"}}>Branding</h2>
            <div>
              <Label className="text-xs">Header logo</Label>
              <div className="mt-2 flex items-center gap-4">
                <div className="h-14 min-w-[120px] px-3 bg-[#F3F4F6] border border-black/5 rounded-xl flex items-center justify-center" data-testid="header-logo-preview">
                  {s.header_logo_url ? (
                    <img src={s.header_logo_url} alt="Header logo" className="max-h-10 w-auto object-contain" />
                  ) : (
                    <span className="text-xs text-[#9CA3AF]">No logo set</span>
                  )}
                </div>
                <div className="flex flex-col gap-1">
                  <Button type="button" variant="outline" size="sm" className="border-black/15" onClick={() => setLogoPickerOpen(true)} data-testid="pick-header-logo-btn">
                    <ImageIcon className="h-4 w-4 mr-1.5" /> {s.header_logo_url ? "Change logo" : "Pick from library"}
                  </Button>
                  {s.header_logo_url && (
                    <Button type="button" variant="ghost" size="sm" className="text-red-500 hover:text-red-600 justify-start px-2"
                            onClick={() => setS({ ...s, header_logo_url: "" })} data-testid="clear-header-logo-btn">
                      <X className="h-3.5 w-3.5 mr-1" /> Clear
                    </Button>
                  )}
                </div>
              </div>
              <p className="text-xs text-[#9CA3AF] mt-2">Recommended: a wide transparent PNG, at least 200px tall. Upload via the media library — opens when you click <em>Change logo</em>.</p>
            </div>
          </div>

          <div className="border-t border-black/5 pt-4 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-medium" style={{fontFamily:"Outfit, sans-serif"}}>Stripe environment</h2>
              <span
                className={`text-xs px-2.5 py-1 rounded-full font-medium uppercase tracking-wider ${
                  s.stripe_mode === "live"
                    ? "bg-[#37B160]/10 text-[#37B160]"
                    : "bg-amber-50 text-amber-700"
                }`}
                data-testid="stripe-mode-badge"
              >
                {s.stripe_mode === "live" ? "LIVE" : "Sandbox (test)"}
              </span>
            </div>
            <p className="text-xs text-[#4B5563]">
              Switch between Stripe's sandbox (test card numbers) and your live keys. Saving the toggle below applies it immediately to every new checkout.
            </p>

            <div className="text-xs rounded-lg px-3 py-2 border"
                 style={stripeStatus.live_configured
                   ? { background:"#F0FDF4", borderColor:"#BBF7D0", color:"#15803D" }
                   : { background:"#FEF2F2", borderColor:"#FECACA", color:"#B91C1C" }}
                 data-testid="stripe-live-key-status">
              {stripeStatus.live_configured ? (
                <>Live secret key configured (sk_live_••••{stripeStatus.live_key_last4}). You can switch to Live whenever you're ready.</>
              ) : (
                <>No live Stripe key detected. Add <code>STRIPE_LIVE_SECRET_KEY</code> to the backend <code>.env</code> before switching to Live.</>
              )}
            </div>

            <div className="text-xs rounded-lg px-3 py-2 border bg-[#F9FAFB] border-black/5 text-[#4B5563]" data-testid="stripe-webhook-status">
              <div className="font-medium text-[#111827] mb-1">Webhook signature verification</div>
              <div className="flex items-center gap-2">
                <span className={stripeStatus.webhook_test_configured ? "text-[#15803D]" : "text-[#B91C1C]"}>
                  {stripeStatus.webhook_test_configured ? "✓" : "✗"} Sandbox
                </span>
                <span className="text-[#9CA3AF]">·</span>
                <span className={stripeStatus.webhook_live_configured ? "text-[#15803D]" : "text-[#B91C1C]"}>
                  {stripeStatus.webhook_live_configured ? "✓" : "✗"} Live
                </span>
              </div>
              {stripeStatus.webhook_endpoint && (
                <div className="mt-1.5 text-[10px] break-all">
                  Endpoint: <code>{stripeStatus.webhook_endpoint}</code>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-2 max-w-md">
              <button
                type="button"
                onClick={() => setS({ ...s, stripe_mode: "test" })}
                data-testid="stripe-mode-test"
                className={`px-4 py-3 rounded-xl border text-sm font-medium transition ${
                  s.stripe_mode !== "live"
                    ? "bg-amber-50 border-amber-300 text-amber-800"
                    : "bg-white border-black/10 text-[#4B5563] hover:border-black/20"
                }`}
              >
                Sandbox (test)
              </button>
              <button
                type="button"
                disabled={!stripeStatus.live_configured && s.stripe_mode !== "live"}
                onClick={() => {
                  if (s.stripe_mode === "live") return;
                  if (!stripeStatus.live_configured) {
                    toast.error("Add STRIPE_LIVE_SECRET_KEY to backend .env first.");
                    return;
                  }
                  const ok = window.confirm("Switch Stripe to LIVE mode? Every checkout will charge real cards and refunds will move real money. You can switch back to Sandbox at any time.");
                  if (ok) setS({ ...s, stripe_mode: "live" });
                }}
                data-testid="stripe-mode-live"
                className={`px-4 py-3 rounded-xl border text-sm font-medium transition ${
                  s.stripe_mode === "live"
                    ? "bg-[#37B160]/10 border-[#37B160] text-[#37B160]"
                    : !stripeStatus.live_configured
                      ? "bg-white border-black/10 text-[#9CA3AF] cursor-not-allowed"
                      : "bg-white border-black/10 text-[#4B5563] hover:border-black/20"
                }`}
              >
                Live (production)
              </button>
            </div>
            {s.stripe_mode === "live" && (
              <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2" data-testid="stripe-live-warning">
                Live mode is active — every checkout will charge real cards. Refunds will hit real money.
              </p>
            )}
          </div>

          <div className="border-t border-black/5 pt-4 space-y-3">
            <h2 className="text-base font-medium" style={{fontFamily:"Outfit, sans-serif"}}>£1 live Stripe test</h2>
            <p className="text-xs text-[#4B5563]">
              Creates a real Stripe Checkout for £1 against any free slot, then runs the full post-payment pipeline (confirmation email, instructor alert, Google Calendar event, refundable order). The checkout uses the <strong>{s.stripe_mode === "live" ? "LIVE" : "Sandbox"}</strong> Stripe mode currently selected above. Refund the £1 from the order ledger once you&apos;ve verified everything.
            </p>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Customer name</Label>
                <Input
                  value={testCheckout.customer_name}
                  onChange={e => setTestCheckout({...testCheckout, customer_name: e.target.value})}
                  placeholder="Test User"
                  data-testid="test-checkout-name"
                />
              </div>
              <div>
                <Label className="text-xs">Customer email</Label>
                <Input
                  type="email"
                  value={testCheckout.customer_email}
                  onChange={e => setTestCheckout({...testCheckout, customer_email: e.target.value})}
                  placeholder="you@example.com"
                  data-testid="test-checkout-email"
                />
              </div>
              <div>
                <Label className="text-xs">Customer phone</Label>
                <Input
                  value={testCheckout.customer_phone}
                  onChange={e => setTestCheckout({...testCheckout, customer_phone: e.target.value})}
                  placeholder="07…"
                  data-testid="test-checkout-phone"
                />
              </div>
              <div>
                <Label className="text-xs">Pickup postcode (optional)</Label>
                <Input
                  value={testCheckout.pickup_postcode}
                  onChange={e => setTestCheckout({...testCheckout, pickup_postcode: e.target.value})}
                  placeholder="LE2 1AA"
                  data-testid="test-checkout-postcode"
                />
              </div>
              <div>
                <Label className="text-xs">Date</Label>
                <Input
                  type="date"
                  value={testCheckout.date}
                  onChange={e => setTestCheckout({...testCheckout, date: e.target.value})}
                  data-testid="test-checkout-date"
                />
              </div>
              <div>
                <Label className="text-xs">Time</Label>
                <select
                  value={testCheckout.time}
                  onChange={e => setTestCheckout({...testCheckout, time: e.target.value})}
                  className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                  data-testid="test-checkout-time"
                >
                  <option value="08:00">08:00</option>
                  <option value="10:45">10:45</option>
                  <option value="13:30">13:30</option>
                  <option value="16:00">16:00 (admin-only)</option>
                </select>
              </div>
            </div>
            <Button
              type="button"
              onClick={createTestCheckout}
              disabled={testCheckoutLoading}
              className="bg-[#37B160] hover:bg-[#2D9450] text-white"
              data-testid="test-checkout-create-btn"
            >
              {testCheckoutLoading ? "Creating…" : "Generate £1 checkout link"}
            </Button>
            {testCheckoutResult && (
              <div className="text-xs rounded-lg px-3 py-3 border bg-[#F0FDF4] border-[#BBF7D0] text-[#15803D] space-y-2" data-testid="test-checkout-result">
                <div>
                  Mode: <strong>{testCheckoutResult.stripe_mode === "live" ? "LIVE" : "Sandbox"}</strong> · Booking: <code>{testCheckoutResult.booking_id}</code>
                </div>
                <a
                  href={testCheckoutResult.checkout_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block underline break-all"
                  data-testid="test-checkout-url"
                >
                  Open £1 Stripe checkout →
                </a>
                <div className="text-[#4B5563]">After paying, you'll land on the booking-success page. The order shows up at <code>/admin/calendar</code> where you can refund the £1.</div>
              </div>
            )}
          </div>

          <div className="border-t border-black/5 pt-4 space-y-3">
            <h2 className="text-base font-medium" style={{fontFamily:"Outfit, sans-serif"}}>Email (Resend)</h2>
            <p className="text-xs text-[#4B5563]">
              Booking confirmations, instructor alerts, cancellations, reschedules and enquiry notifications are sent through Resend.
              Use this to send yourself a quick test once your sender domain has finished verifying.
            </p>
            <div className="text-xs rounded-lg px-3 py-2 border bg-[#F9FAFB] border-black/5 text-[#4B5563]">
              Sender (from <code>.env</code>): <code data-testid="email-sender-display">configured server-side</code>
              <br />
              If sends fail, the most common cause is <strong>sender domain DNS not yet propagated</strong> in Resend.
            </div>
            <div className="flex flex-wrap gap-2 items-center">
              <Input
                type="email"
                value={testEmailTo}
                onChange={e => setTestEmailTo(e.target.value)}
                placeholder="you@example.com"
                className="max-w-xs"
                data-testid="test-email-input"
              />
              <Button
                type="button"
                onClick={sendTestEmail}
                disabled={testEmailSending}
                className="bg-[#37B160] hover:bg-[#2D9450] text-white"
                data-testid="send-test-email-btn"
              >
                {testEmailSending ? "Sending…" : "Send test email"}
              </Button>
            </div>

            <div className="border-t border-black/5 pt-3 mt-3">
              <p className="text-xs text-[#4B5563] mb-1">
                <strong>Inbound forwarding.</strong> Any email sent to <code>info@limedrivingschool.com</code> is captured by
                Resend and can be forwarded to your Instructor email above. To enable it:
              </p>
              <ol className="text-xs text-[#4B5563] list-decimal pl-5 space-y-1">
                <li>
                  In Resend → <em>Webhooks</em> → <em>Add Webhook</em>, set the URL to:
                  <br /><code className="text-[11px] bg-[#F9FAFB] px-1 py-0.5 rounded border border-black/5">https://limedrivingschool.com/api/webhook/resend-inbound</code>
                </li>
                <li>Select event <code>email.received</code> and save.</li>
                <li>Copy the signing secret (<code>whsec_…</code>) and add it to your <strong>production</strong> environment as <code>RESEND_WEBHOOK_INBOUND_SECRET</code>.</li>
                <li>Redeploy. Inbound mail will then automatically forward to the Instructor email saved below.</li>
              </ol>
            </div>
          </div>

          <div className="border-t border-black/5 pt-4 space-y-3">
            <h2 className="text-base font-medium" style={{fontFamily:"Outfit, sans-serif"}}>Google Calendar sync</h2>
            <p className="text-xs text-[#4B5563]">
              New bookings are pushed to Google Calendar automatically. If you've just imported historical
              bookings (or events are missing for any reason), use this button to push everything that
              doesn't yet have a calendar event. This is safe to re-run — existing events are detected and
              skipped to prevent duplicates.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                onClick={() => syncMissingGCal(true)}
                disabled={gcalSyncing}
                className="bg-[#37B160] hover:bg-[#2D9450] text-white"
                data-testid="gcal-sync-imports-btn"
              >
                {gcalSyncing ? "Pushing to Google Calendar…" : "Push imported bookings to Google Calendar"}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => syncMissingGCal(false)}
                disabled={gcalSyncing}
                className="border-black/15"
                data-testid="gcal-sync-all-btn"
              >
                Push every missing booking
              </Button>
            </div>
            {gcalResult && (
              <div className="text-xs rounded-lg px-3 py-2 border bg-[#F9FAFB] border-black/5 text-[#4B5563]" data-testid="gcal-sync-result">
                Examined <strong>{gcalResult.examined}</strong> · Synced <strong className="text-[#15803D]">{gcalResult.synced}</strong>
                {gcalResult.failed > 0 && <> · Failed <strong className="text-[#B91C1C]">{gcalResult.failed}</strong></>}
              </div>
            )}

            <div className="border-t border-black/5 pt-3 mt-2">
              <p className="text-xs text-[#4B5563] mb-2">
                <strong>Block-dates / holidays</strong> also mirror to Google Calendar — fully-blocked days appear as an all-day event, partially-blocked days as one timed event per slot. Use this to reconcile every existing block in one go.
              </p>
              <Button
                type="button"
                variant="outline"
                onClick={syncHolidaysGCal}
                disabled={holidaySyncing}
                className="border-black/15"
                data-testid="gcal-sync-holidays-btn"
              >
                {holidaySyncing ? "Reconciling holidays…" : "Push blocked dates to Google Calendar"}
              </Button>
              {holidayResult && (
                <div className="text-xs rounded-lg px-3 py-2 border bg-[#F9FAFB] border-black/5 text-[#4B5563] mt-2" data-testid="gcal-holiday-result">
                  Dates <strong>{holidayResult.examined_dates}</strong> · Created <strong className="text-[#15803D]">{holidayResult.created}</strong> · Updated <strong>{holidayResult.updated}</strong> · Cleaned up <strong>{holidayResult.deleted}</strong>
                  {holidayResult.failed > 0 && <> · Failed <strong className="text-[#B91C1C]">{holidayResult.failed}</strong></>}
                </div>
              )}
            </div>

            <div className="border-t border-red-100 pt-3 mt-2 bg-red-50/60 -mx-2 px-2 rounded-lg">
              <p className="text-xs text-[#4B5563] mb-2">
                <strong className="text-red-700">Reset &amp; re-sync calendar.</strong> Deletes <em>every</em> event the app has previously written to Google Calendar (identified by our internal tags) and rebuilds them from this site's database. Use this if you've ended up with duplicate events from syncing across multiple environments (preview + production). Events you added manually in Google Calendar are <strong>not</strong> touched.
              </p>
              <p className="text-[11px] text-[#9CA3AF] mb-2">
                With a large calendar this can take a few minutes. If the page appears to hang, the cleanup continues server-side — refresh the page after a couple of minutes and check Google Calendar directly to confirm.
              </p>
              <Button
                type="button"
                variant="outline"
                onClick={resetAndResync}
                disabled={resetting}
                className="border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800"
                data-testid="gcal-reset-resync-btn"
              >
                {resetting ? "Resetting calendar…" : "Reset & re-sync calendar"}
              </Button>
              {resetResult && (
                <div className="text-xs rounded-lg px-3 py-2 border bg-white border-red-100 text-[#4B5563] mt-2 space-y-1" data-testid="gcal-reset-result">
                  {resetResult.stage && resetResult.status !== "completed" && (
                    <div>Stage: <strong className="text-red-700">{({
                      purging: "Purging old events…",
                      clearing_ids: "Clearing event IDs from bookings…",
                      resyncing_bookings: "Re-creating booking events…",
                      resyncing_holidays: "Re-creating holiday events…",
                      done: "Finalising…",
                    })[resetResult.stage] || resetResult.stage}</strong></div>
                  )}
                  {resetResult.purged && (
                    <div>
                      Purged: <strong>{resetResult.purged?.booking_events_deleted || 0}</strong> booking events + <strong>{resetResult.purged?.holiday_events_deleted || 0}</strong> holiday events
                    </div>
                  )}
                  <div>
                    Re-synced: <strong className="text-[#15803D]">{resetResult.bookings_resynced || 0}</strong> bookings · <strong className="text-[#15803D]">{resetResult.holiday_dates_resynced || 0}</strong> blocked dates
                  </div>
                  {((resetResult.bookings_failed || 0) + (resetResult.holiday_failed || 0) + (resetResult.purged?.failed || 0)) > 0 && (
                    <div><strong className="text-[#B91C1C]">{(resetResult.bookings_failed || 0) + (resetResult.holiday_failed || 0) + (resetResult.purged?.failed || 0)} failures (see logs)</strong></div>
                  )}
                  {resetResult.status === "completed" && (
                    <div className="text-[#15803D] font-medium pt-1">✓ Completed</div>
                  )}
                  {resetResult.status === "failed" && (
                    <div className="text-[#B91C1C] font-medium pt-1">✗ Failed: {resetResult.error}</div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="border-t border-black/5 pt-4 space-y-3">
            <h2 className="text-base font-medium" style={{fontFamily:"Outfit, sans-serif"}}>Instructor details</h2>
            <div>
              <Label className="text-xs">Name</Label>
              <Input value={s.instructor_name} onChange={e => setS({...s, instructor_name: e.target.value})} data-testid="settings-name" />
            </div>
            <div>
              <Label className="text-xs">Phone</Label>
              <Input value={s.instructor_phone} onChange={e => setS({...s, instructor_phone: e.target.value})} data-testid="settings-phone" />
            </div>
            <div>
              <Label className="text-xs">Email (booking alerts &amp; enquiries go here)</Label>
              <Input value={s.instructor_email} onChange={e => setS({...s, instructor_email: e.target.value})} data-testid="settings-email" />
            </div>
          </div>

          <Button onClick={onSave} disabled={saving} className="bg-[#37B160] hover:bg-[#2D9450] text-white" data-testid="settings-save-btn">
            {saving ? "Saving…" : "Save settings"}
          </Button>
        </div>

        {/* Live price preview */}
        <div className="lg:col-span-5">
          <div className="bg-white border border-black/5 rounded-2xl p-6 sticky top-6">
            <h2 className="text-base font-medium" style={{fontFamily:"Outfit, sans-serif"}}>Live price preview</h2>
            <p className="text-xs text-[#4B5563] mt-1">Updates as you edit. This is exactly what customers see.</p>
            {["manual", "auto"].map(tx => (
              <div key={tx} className="mt-5">
                <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#9CA3AF] mb-2">{tx === "manual" ? "Manual" : "Automatic"}</div>
                <table className="w-full text-sm">
                  <thead><tr className="text-[#4B5563] text-xs">
                    <th className="text-left font-normal pb-1">Product</th>
                    <th className="text-right font-normal pb-1">£/hr</th>
                    <th className="text-right font-normal pb-1">Total</th>
                  </tr></thead>
                  <tbody>
                    {[
                      { key: "standard", label: "Standard 2-hour" },
                      { key: "package10", label: "5 lessons (10h)" },
                      { key: "package20", label: "10 lessons (20h)" },
                    ].map(p => {
                      const { rate, total, discount } = previewPrice(slotsForProduct[p.key], tx);
                      return (
                        <tr key={p.key} className="border-t border-black/5" data-testid={`preview-${tx}-${p.key}`}>
                          <td className="py-2">
                            {p.label}
                            {discount > 0 && <span className="block text-[10px] text-[#37B160]">−£{discount.toFixed(2)}/hr</span>}
                          </td>
                          <td className="py-2 text-right">£{rate.toFixed(2)}</td>
                          <td className="py-2 text-right font-medium">£{total.toFixed(2)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        </div>
      </div>

      <MediaPicker
        open={logoPickerOpen}
        onClose={() => setLogoPickerOpen(false)}
        onPick={(item) => { setS(prev => ({ ...prev, header_logo_url: item.url })); setLogoPickerOpen(false); }}
      />
    </div>
  );
}
